import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
  addEdge,
  type Connection,
  type Edge,
  type Node,
  type NodeMouseHandler
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Database,
  Globe,
  GitBranch,
  List,
  CheckCircle,
  type LucideIcon
} from 'lucide-react'

import type { FlowConfigData } from '../../types/flowConfig.types'
import type { FlowStep } from '../../types/flowConfig.types'
import { updateFlowStepInConfig } from '../../utils/flowStepUpdate'
import { validateFlowConfigForEditor } from '../../utils/flowGraphValidation'
import {
  flowConfigToGraph,
  graphToFlowConfig,
  nodesToUiLayout,
  addStepToFlowConfig,
  type FlowCanvasNodeData
} from './flowGraphAdapter'
import { StepNode } from './StepNode'
import { StepEditor } from '../FlowEditor'
import type { FlowConfigUpdater } from '../../types/flowConfigUpdater'
import { flowConfigCanvasSignature, removeStepsFromFlowConfig } from '../../utils/flowUiLayout'

const nodeTypes = { flowStep: StepNode }

const STEP_TYPE_LABELS: Record<FlowStep['type'], string> = {
  data_collection: 'Coleta de dados',
  api_call: 'Chamada API',
  conditional: 'Condicional',
  dynamic_conditional: 'Condicional dinâmica',
  list_selection: 'Seleção de lista',
  dynamic_list: 'Lista dinâmica',
  item_selection: 'Seleção de item',
  message_dispatch: 'Despacho de mensagem',
  credential_ref: 'Credencial externa',
  auth_session: 'Sessão de autenticação',
  transfer_human: 'Transferir humano',
  finalization: 'Finalização'
}

const STEP_TYPE_ICONS: Record<FlowStep['type'], LucideIcon> = {
  data_collection: Database,
  api_call: Globe,
  conditional: GitBranch,
  dynamic_conditional: GitBranch,
  list_selection: List,
  dynamic_list: List,
  item_selection: List,
  message_dispatch: CheckCircle,
  credential_ref: Database,
  auth_session: Globe,
  transfer_human: CheckCircle,
  finalization: CheckCircle
}

const STEP_TYPE_COLORS: Record<FlowStep['type'], string> = {
  data_collection: 'bg-blue-500',
  api_call: 'bg-green-600',
  conditional: 'bg-amber-500',
  dynamic_conditional: 'bg-orange-500',
  list_selection: 'bg-purple-500',
  dynamic_list: 'bg-violet-600',
  item_selection: 'bg-violet-700',
  message_dispatch: 'bg-indigo-600',
  credential_ref: 'bg-cyan-600',
  auth_session: 'bg-emerald-600',
  transfer_human: 'bg-teal-500',
  finalization: 'bg-red-500'
}

/** Paleta lateral por categorias (Fase 3). */
const PALETTE_GROUPS: { title: string; types: FlowStep['type'][] }[] = [
  { title: 'Entrada e autenticação', types: ['data_collection', 'credential_ref', 'auth_session'] },
  { title: 'Integração e listas', types: ['api_call', 'list_selection', 'dynamic_list', 'item_selection'] },
  { title: 'Lógica e mensagens', types: ['conditional', 'dynamic_conditional', 'message_dispatch'] },
  { title: 'Encerramento', types: ['transfer_human', 'finalization'] }
]

export interface FlowVisualEditorProps {
  flowConfig: FlowConfigData
  onUiLayoutChange?: (layout: Record<string, { x: number; y: number }>) => void
  /** Preferir atualizações funcionais `(prev) => next` ao adicionar passos ou fundir após remover nos Formulários. */
  onFlowConfigChange?: (update: FlowConfigUpdater) => void
  editable?: boolean
  className?: string
}

function FlowVisualCanvas({
  flowConfig,
  onUiLayoutChange,
  onFlowConfigChange,
  editable = true,
  className
}: FlowVisualEditorProps) {
  const { fitView, getNodes, getEdges } = useReactFlow()
  const flowConfigRef = useRef(flowConfig)
  flowConfigRef.current = flowConfig

  const lastPaletteAddAtRef = useRef(0)
  const lastCanvasSyncSigRef = useRef<string>('')

  const initialFitDone = useRef(false)
  const prevStepCountRef = useRef(0)

  const [inspectorStepId, setInspectorStepId] = useState<string | null>(null)

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => flowConfigToGraph(flowConfig),
    [flowConfig]
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  const validationIssues = useMemo(() => validateFlowConfigForEditor(flowConfig), [flowConfig])
  const validationErrors = validationIssues.filter((i) => i.level === 'error')
  const validationWarnings = validationIssues.filter((i) => i.level === 'warning')

  const emitFlowChange = useCallback(
    (update: FlowConfigUpdater) => {
      onFlowConfigChange?.(update)
    },
    [onFlowConfigChange]
  )

  const syncFromGraph = useCallback(
    (nextNodes: Node[], nextEdges: Edge[], startStep: string) => {
      emitFlowChange((prev) => graphToFlowConfig(prev, nextNodes, nextEdges, startStep))
    },
    [emitFlowChange]
  )

  const setStartStep = useRef<(id: string) => void>(() => {})
  setStartStep.current = (id: string) => {
    const valid = new Set(Object.keys(flowConfigRef.current.steps || {}))
    const n = getNodes().filter((node) => valid.has(node.id))
    syncFromGraph(n, getEdges(), id)
  }

  const attachNodeCallbacks = useCallback(
    (raw: Node<FlowCanvasNodeData>[]): Node<FlowCanvasNodeData>[] =>
      editable
        ? raw.map((n) => ({
            ...n,
            data: {
              ...n.data,
              onSetAsStart: () => setStartStep.current(n.id)
            }
          }))
        : raw.map((n) => ({
            ...n,
            data: { ...n.data, onSetAsStart: undefined }
          })),
    [editable]
  )

  /** Quando o conjunto de ids muda (ex.: delete nos Formulários), forçar re-sync — evita nós órfãos no estado interno do RF. */
  const stepIdsKey = Object.keys(flowConfig.steps || {})
    .sort()
    .join('|')
  useEffect(() => {
    lastCanvasSyncSigRef.current = ''
  }, [stepIdsKey])

  useEffect(() => {
    const sig = `${flowConfigCanvasSignature(flowConfig)}|editable=${editable}`
    const stepCount = Object.keys(flowConfig.steps || {}).length
    const rfNodeCount = getNodes().length
    /** Assinatura pode ser igual após remover etapas nos Formulários (layout podado igual), mas o RF ainda tem nós órfãos — é preciso `setNodes`. */
    const structureMismatch = stepCount !== rfNodeCount
    if (!structureMismatch && sig === lastCanvasSyncSigRef.current) {
      return undefined
    }
    lastCanvasSyncSigRef.current = sig

    const { nodes: nextRaw, edges: nextEdges } = flowConfigToGraph(flowConfig)
    setNodes(attachNodeCallbacks(nextRaw))
    setEdges(nextEdges)
    const firstStepsAdded = prevStepCountRef.current === 0 && stepCount > 0
    if (!initialFitDone.current || firstStepsAdded) {
      initialFitDone.current = true
      const t = requestAnimationFrame(() => {
        fitView({ padding: 0.15, duration: 250 })
      })
      prevStepCountRef.current = stepCount
      return () => cancelAnimationFrame(t)
    }
    prevStepCountRef.current = stepCount
    return undefined
    /* getNodes() é lido no efeito para detetar órfãos; omitido nas deps para evitar re-sync em loop. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowConfig, editable, setNodes, setEdges, fitView, attachNodeCallbacks])

  useEffect(() => {
    if (inspectorStepId && !flowConfig.steps?.[inspectorStepId]) {
      setInspectorStepId(null)
    }
  }, [flowConfig.steps, inspectorStepId])

  const handleInspectorUpdate = useCallback(
    (stepId: string, updates: Partial<FlowStep>) => {
      emitFlowChange((prev) => {
        const result = updateFlowStepInConfig(prev, stepId, updates)
        if (!result) return prev
        if (result.renamedTo) {
          queueMicrotask(() => setInspectorStepId(result.renamedTo!))
        }
        return result.config
      })
    },
    [emitFlowChange]
  )

  const handleNodeDragStop = useCallback(() => {
    const validIds = new Set(Object.keys(flowConfigRef.current.steps || {}))
    const nodeSnap = getNodes().filter((n) => validIds.has(n.id))
    const edgeSnap = getEdges().filter(
      (e) => validIds.has(e.source) && validIds.has(e.target)
    )
    onUiLayoutChange?.(nodesToUiLayout(nodeSnap))
    if (editable && onFlowConfigChange) {
      emitFlowChange((prev) => graphToFlowConfig(prev, nodeSnap, edgeSnap, prev.start_step))
    }
  }, [getNodes, getEdges, onUiLayoutChange, editable, onFlowConfigChange, emitFlowChange])

  const isValidConnection = useCallback((connection: Edge | Connection) => {
    const ids = new Set(Object.keys(flowConfigRef.current.steps || {}))
    const source = connection.source
    const target = connection.target
    return !!(
      source &&
      target &&
      ids.has(source) &&
      ids.has(target) &&
      source !== target
    )
  }, [])

  const onConnect = useCallback(
    (c: Connection) => {
      if (!editable || !c.source || !c.target) return
      const handle = c.sourceHandle || 'next'
      setEdges((eds) => {
        const filtered = eds.filter(
          (e) => !(e.source === c.source && (e.sourceHandle || 'next') === handle)
        )
        const newEdge: Edge = {
          id: `e-${c.source}-${c.target}-${handle}`,
          source: c.source,
          target: c.target,
          sourceHandle: c.sourceHandle ?? undefined,
          type: 'smoothstep',
          style: { stroke: '#64748b' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' }
        }
        const next = addEdge(newEdge, filtered)
        queueMicrotask(() => {
          const validStepIds = new Set(Object.keys(flowConfigRef.current.steps || {}))
          const n = getNodes().filter((node) => validStepIds.has(node.id))
          syncFromGraph(n, next, flowConfigRef.current.start_step)
        })
        return next
      })
    },
    [editable, setEdges, syncFromGraph, getNodes]
  )

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (!editable) return
      setEdges((eds) => {
        const ids = new Set(deleted.map((e) => e.id))
        const next = eds.filter((e) => !ids.has(e.id))
        queueMicrotask(() => {
          const validStepIds = new Set(Object.keys(flowConfigRef.current.steps || {}))
          const n = getNodes().filter((node) => validStepIds.has(node.id))
          syncFromGraph(n, next, flowConfigRef.current.start_step)
        })
        return next
      })
    },
    [editable, setEdges, syncFromGraph, getNodes]
  )

  /**
   * Sem isto, a tecla Delete só remove nós do estado interno do React Flow; `flow_config.steps`
   * mantém-se e os blocos “voltam” ao adicionar outro na paleta. Remover nos Formulários funcionava
   * porque o FlowEditor atualiza `steps` de facto.
   */
  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      if (!editable || !onFlowConfigChange || deleted.length === 0) return
      const ids = new Set(deleted.map((n) => n.id))
      emitFlowChange((prev) => removeStepsFromFlowConfig(prev, ids))
      setInspectorStepId((cur) => (cur && ids.has(cur) ? null : cur))
    },
    [editable, onFlowConfigChange, emitFlowChange]
  )

  /** Posição X máxima só a partir do `flowConfig` — `getNodes()` pode ainda listar nós de etapas já removidas antes do efeito de sync. */
  const maxXFromFlowConfig = useCallback((cfg: FlowConfigData) => {
    const { nodes } = flowConfigToGraph(cfg)
    if (nodes.length === 0) return 0
    return nodes.reduce((m, n) => Math.max(m, n.position.x), 0)
  }, [])

  const handleAddStep = useCallback(
    (type: FlowStep['type']) => {
      if (!editable || !onFlowConfigChange) return
      const now = Date.now()
      if (now - lastPaletteAddAtRef.current < 400) return
      lastPaletteAddAtRef.current = now
      const stepId = `step_${now}_${Math.random().toString(36).slice(2, 11)}`
      const maxX = maxXFromFlowConfig(flowConfigRef.current)
      emitFlowChange((prev) =>
        addStepToFlowConfig(prev, type, { x: maxX + 280, y: 80 }, 'step', stepId)
      )
      setInspectorStepId(stepId)
    },
    [editable, onFlowConfigChange, emitFlowChange, maxXFromFlowConfig]
  )

  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    setInspectorStepId(node.id)
  }, [])

  const onPaneClick = useCallback(() => {
    setInspectorStepId(null)
  }, [])

  const nodeCount = Object.keys(flowConfig.steps || {}).length
  const inspectorStep = inspectorStepId ? flowConfig.steps?.[inspectorStepId] : undefined
  const availableStepsForEditor = useMemo(
    () =>
      Object.entries(flowConfig.steps || {}).map(([id, s]) => ({
        id,
        name: s?.name || id
      })),
    [flowConfig.steps]
  )

  const canvasHeightClass = 'h-[min(70vh,560px)] min-h-[320px]'

  const helperText = editable
    ? 'Paleta: novos blocos. Com nós selecionados, Delete remove as etapas do fluxo; só arestas: selecione a ligação e Delete. Também pode remover etapas no separador Formulários.'
    : 'Arraste os cartões para organizar. Alterações de ligações e propriedades no modo editável.'

  return (
    <div className={className ?? ''}>
      {(validationErrors.length > 0 || validationWarnings.length > 0) && (
        <div className="mb-2 space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
          {validationErrors.map((e, i) => (
            <p key={`e-${i}`} className="text-red-700">
              {e.message}
            </p>
          ))}
          {validationWarnings.map((w, i) => (
            <p key={`w-${i}`} className="text-amber-800">
              {w.message}
            </p>
          ))}
        </div>
      )}

      <div
        className={`flex flex-col lg:flex-row gap-0 rounded-lg border border-slate-200 bg-white overflow-hidden ${canvasHeightClass}`}
      >
        {editable ? (
          <aside
            className="w-full lg:w-52 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-slate-50 flex flex-col max-h-[40vh] lg:max-h-none overflow-y-auto"
            aria-label="Paleta de blocos"
          >
            <p className="text-xs font-semibold text-slate-700 px-3 py-2 border-b border-slate-200 bg-slate-100/80">
              Blocos
            </p>
            <div className="p-2 space-y-3">
              {PALETTE_GROUPS.map((group) => (
                <div key={group.title}>
                  <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400 px-1 mb-1.5">
                    {group.title}
                  </p>
                  <div className="space-y-1">
                    {group.types.map((t) => {
                      const Icon = STEP_TYPE_ICONS[t]
                      const color = STEP_TYPE_COLORS[t]
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => handleAddStep(t)}
                          className="w-full flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-xs text-slate-800 hover:bg-white hover:border-slate-200 hover:shadow-sm transition-colors"
                        >
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${color}`}>
                            <Icon className="h-3.5 w-3.5 text-white" aria-hidden />
                          </span>
                          <span className="leading-tight">{STEP_TYPE_LABELS[t]}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        ) : null}

        <div className="flex-1 min-w-0 min-h-[280px] flex flex-col bg-slate-50/80">
          {nodeCount === 0 ? (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-slate-500">
              Sem etapas. Use a paleta à esquerda ou o separador <strong>Formulários</strong>.
            </div>
          ) : (
            <div className={`flex-1 w-full overflow-visible ${canvasHeightClass}`}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={editable ? onConnect : undefined}
                onEdgesDelete={editable ? onEdgesDelete : undefined}
                onNodesDelete={editable ? onNodesDelete : undefined}
                onNodeDragStop={handleNodeDragStop}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                isValidConnection={isValidConnection}
                nodeTypes={nodeTypes}
                nodesDraggable
                nodesConnectable={editable}
                edgesReconnectable={false}
                edgesFocusable={editable}
                elementsSelectable
                deleteKeyCode={editable ? 'Delete' : null}
                panOnScroll
                zoomOnScroll
                fitView
                proOptions={{ hideAttribution: true }}
                defaultEdgeOptions={{ type: 'smoothstep' }}
              >
                <Background variant={BackgroundVariant.Dots} gap={14} size={1} color="#cbd5e1" />
                <Controls className="!border-slate-200 !shadow-sm" showInteractive={false} />
                <MiniMap
                  className="!border !border-slate-200 !rounded-md !bg-white"
                  nodeColor={() => '#e2e8f0'}
                  maskColor="rgba(148, 163, 184, 0.12)"
                />
              </ReactFlow>
            </div>
          )}
        </div>

        {editable ? (
          <aside
            className="w-full lg:w-[min(420px,40vw)] shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white flex flex-col max-h-[50vh] lg:max-h-none"
            aria-label="Propriedades da etapa"
          >
            <div className="shrink-0 border-b border-slate-100 px-3 py-2 bg-slate-50/90">
              <h3 className="text-sm font-semibold text-slate-900">Propriedades da etapa</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Os mesmos campos do separador Formulários, por tipo de passo.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 p-3">
              {inspectorStep && inspectorStepId ? (
                <StepEditor
                  step={inspectorStep}
                  onUpdate={(updates) => handleInspectorUpdate(inspectorStepId, updates)}
                  availableSteps={availableStepsForEditor}
                />
              ) : (
                <p className="text-sm text-slate-500 py-8 text-center">
                  Clique num nó no diagrama para editar nome, ID, configuração e ligações textuais.
                </p>
              )}
            </div>
          </aside>
        ) : null}
      </div>

      <p className="mt-2 text-xs text-slate-500">{helperText}</p>
    </div>
  )
}

export function FlowVisualEditor(props: FlowVisualEditorProps) {
  return (
    <ReactFlowProvider>
      <FlowVisualCanvas {...props} />
    </ReactFlowProvider>
  )
}
